import * as React from "react";
import classNames from "classnames";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { Link, Route, Switch, withRouter } from "react-router-dom";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { translate, InjectedTranslateProps } from "react-i18next";

import AppBarCore from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import AccountCircle from "@material-ui/icons/AccountCircle";
import MenuIcon from "@material-ui/icons/Menu";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import {
	showAdditionalInfo,
	hideAdditionalInfo,
	toggleInfoBar,
	setActiveArea
} from "../../actions/store/overview-actions";
import { Capabilities } from "../../integration/capabilities";
import { toggleDrawer } from "../../actions/store/ui-actions";
import { ConnectedPlannerItems } from "./planner-items";
import { ConnectedInquiryListItems } from "./inquiry-list-items";
import { InjectedRouterProps } from "../../router/props";
import { setLoginStatusAndAuthInfo } from "../../actions/auth-actions";
import { Routes } from "../../router/routes";
import { ConnectedPrintViewItems } from "./print-view-items";
import { ConnectedMobileMenu } from "./mobile-menu";
import { ConnectedDashboardItems } from "./dashboard-items";
import { ConnectedPaymentItems } from './payment-items';
import { isIPhoneX } from '../../util/device-util';

export interface AppBarProps {}

interface State {
	profileMenuAnchor: HTMLElement;
}

interface ConnectedAppBarProps extends WithStyles<typeof styles> {
	capabilities: Capabilities;
	layout: "desktop" | "tablet" | "phone";
	toggleDrawer: () => void;
}

type Props = AppBarProps &
	ConnectedAppBarProps &
	InjectedTranslateProps &
	InjectedRouterProps;

export class AppBar extends React.Component<Props, State> {
	public state: State = {
		profileMenuAnchor: null
	};

	private handleProfileButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		this.setState({ profileMenuAnchor: event.currentTarget });
	};

	private handleProfileMenuClose = () => {
		this.setState({ profileMenuAnchor: null });
	};

	private handleLogoutMenuItemClick = () => {
		sessionStorage.removeItem("token");
		sessionStorage.removeItem("authInfo");

		setLoginStatusAndAuthInfo(false);
	};

	private handleMenuItemClick = () => {
		this.props.toggleDrawer();
	};

	private renderMenuIconAndTitle() {
		const { classes, layout } = this.props;

		return (
			<React.Fragment>
				<IconButton
					id="toggle-drawer-button"
					className={classes.menuButton}
					onClick={this.handleMenuItemClick}
				>
					<MenuIcon className={classes.menuIcon} />
				</IconButton>
				{layout !== "phone" && (
					<Link className={classes.appTitleLink} to="/">
						<Typography
							className={classes.appTitle}
							variant="title"
							color="inherit"
						>
							Pinzgauer Jägerball
						</Typography>
					</Link>
				)}
			</React.Fragment>
		);
	}

	private renderProfileIcon() {
		const { classes } = this.props;
		const { profileMenuAnchor } = this.state;

		return (
			<React.Fragment>
				<IconButton
					color="inherit"
					onClick={this.handleProfileButtonClick}
				>
					<AccountCircle />
				</IconButton>
				<Menu
					anchorEl={profileMenuAnchor}
					open={Boolean(profileMenuAnchor)}
					onClick={this.handleProfileMenuClose}
				>
					<MenuItem
						className={classes.logoutMenuItem}
						onClick={this.handleLogoutMenuItemClick}
					>
						Abmelden
					</MenuItem>
				</Menu>
			</React.Fragment>
		);
	}

	public render() {
		const { classes, capabilities, layout } = this.props;

		return (
			<React.Fragment>
				<AppBarCore
					className={classes.appBar}
				>
					<Toolbar variant="dense" className={classes.toolbar}>
						{this.renderMenuIconAndTitle()}

						<React.Fragment>
							<Switch>
								<Route
									path={Routes.Dashboard}
									component={ConnectedDashboardItems}
								/>
								<Route
									path={Routes.BookingPlan}
									component={ConnectedPlannerItems}
								/>
								<Route
									path={Routes.InquiryList}
									component={ConnectedInquiryListItems}
								/>
								<Route
									path={Routes.Payments}
									component={ConnectedPaymentItems}
								/>
								<Route
									path={Routes.PrintViews}
									component={ConnectedPrintViewItems}
								/>
								<Route>
									<React.Fragment>
										<div className={classes.flexExpand} />
										{layout === "phone" && (
											<ConnectedMobileMenu />
										)}
									</React.Fragment>
								</Route>
							</Switch>
							{layout !== "phone" && this.renderProfileIcon()}
						</React.Fragment>
					</Toolbar>
				</AppBarCore>
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	capabilities: state.capabilities,
	layout: state.layout
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	toggleDrawer: () => dispatch(toggleDrawer())
});

const styles = (theme: Theme) =>
	createStyles({
		appBar: {
			zIndex: theme.zIndex.drawer + 1
		},
		toolbar: {
			paddingLeft: 0,
			paddingRight: 0
		},
		appTitle: {
			paddingRight: 10
		},
		appTitleLink: {
			color: "black",
			textDecoration: "none"
		},
		logoutMenuItem: {},
		menuIcon: {
			color: "black"
		},
		menuButton: {},
		flexExpand: {
			flexGrow: 1
		}
	});

export const ConnectedAppBar = translate("translations")(
	withRouter(
		withStyles(styles)(
			connect(
				mapStateToProps,
				mapDispatchToProps
			)(AppBar)
		)
	)
);
